import type { RestrictivePractice } from '@lmc/validations'
import dayjs, { type Dayjs } from 'dayjs'
import { getCombinedTimeIso } from '../../../../../utils/incidentUtils'

/** `time` on `from`'s day, or on the next day when that is before `from`. */
const onOrAfter = (from: Dayjs, time: Dayjs) => {
  const instant = dayjs(getCombinedTimeIso(from, time))
  return instant.isBefore(from.startOf('minute'))
    ? instant.add(1, 'day')
    : instant
}

/**
 * How long before the incident's recorded start a practice can begin and
 * still be taken as the same occasion: that start is often an estimate.
 */
const START_LEEWAY_HOURS = 12

/**
 * `time` on whichever day puts it from 12 hours before the incident's start
 * to 12 hours after it.
 */
const earliestStart = (incidentStart: Dayjs) =>
  incidentStart.startOf('minute').subtract(START_LEEWAY_HOURS, 'hour')

/** Whether `instant` is already where `nearIncident` would put it. */
const isNearIncident = (incidentStart: Dayjs, instant: Dayjs) =>
  !instant.isBefore(earliestStart(incidentStart)) &&
  instant.isBefore(earliestStart(incidentStart).add(1, 'day'))

const nearIncident = (incidentStart: Dayjs, time: Dayjs) => {
  const instant = dayjs(getCombinedTimeIso(incidentStart, time))
  const earliest = earliestStart(incidentStart)
  if (instant.isBefore(earliest)) {
    return instant.add(1, 'day')
  }
  return instant.isBefore(earliest.add(1, 'day'))
    ? instant
    : instant.subtract(1, 'day')
}

/**
 * A practice's clock times as instants. The picker has no date, so a start
 * falls within 12 hours either side of the incident's start, and an end
 * before its own start is the next day.
 */
export const toPracticeTimes = (
  incidentStart: Dayjs,
  start: Dayjs | null,
  end: Dayjs | null
): Pick<RestrictivePractice, 'endedAt' | 'startedAt'> => {
  if (!start) {
    return {}
  }
  const startedAt = nearIncident(incidentStart, start)
  return {
    startedAt: startedAt.toISOString(),
    ...(end ? { endedAt: onOrAfter(startedAt, end).toISOString() } : {})
  }
}

/** Whether `instant` is already `onOrAfter(from, instant)`: within a day after `from`. */
const isWithinDayAfter = (from: Dayjs, instant: Dayjs) =>
  !instant.isBefore(from.startOf('minute')) &&
  instant.isBefore(from.startOf('minute').add(1, 'day'))

/**
 * Re-applies `toPracticeTimes`, so practices follow a change to the incident's
 * start. A saved time that still falls where it would is kept exactly: working
 * it out again from the clock time loses its milliseconds and, in the hour the
 * clocks go back, moves it an hour.
 */
export const rebasePractice = (
  practice: RestrictivePractice,
  incidentStart: Dayjs
): RestrictivePractice => {
  const { endedAt, startedAt, ...rest } = practice
  if (!startedAt) {
    return rest
  }
  const savedStart = dayjs(startedAt)
  const start = isNearIncident(incidentStart, savedStart)
    ? savedStart
    : nearIncident(incidentStart, savedStart)
  const savedEnd = endedAt ? dayjs(endedAt) : null
  const end =
    savedEnd &&
    (isWithinDayAfter(start, savedEnd) ? savedEnd : onOrAfter(start, savedEnd))

  return {
    ...rest,
    startedAt: start.toISOString(),
    ...(end ? { endedAt: end.toISOString() } : {})
  }
}
